/**
 * Status Page Service
 * Handles all status page operations including components, incidents, maintenance, and subscriptions
 */

import {
    collection,
    doc,
    getDocs,
    getDoc,
    addDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    limit,
    onSnapshot,
    Timestamp,
    serverTimestamp,
    Unsubscribe
} from 'firebase/firestore';
import { db } from '../firebase';

// ============================================================================
// TYPES
// ============================================================================

export type ComponentStatus = 'operational' | 'degraded' | 'partial_outage' | 'major_outage' | 'maintenance';
export type IncidentStatus = 'investigating' | 'identified' | 'monitoring' | 'resolved';
export type IncidentImpact = 'none' | 'minor' | 'major' | 'critical';
export type MaintenanceStatus = 'scheduled' | 'in_progress' | 'completed';
export type ComponentCategory = 'channels' | 'platforms' | 'product_areas';

export interface StatusComponent {
    id: string;
    name: string;
    description: string;
    category: ComponentCategory;
    status: ComponentStatus;
    order: number;
    isActive: boolean;
    showUptimeChart: boolean;
    lastCheckedAt: Timestamp | null;
    lastStatusChangeAt: Timestamp | null;
    uptimePercentage30Days: number;
    uptimePercentage90Days: number;
    dailyUptime: DailyUptime[];
    createdAt: Timestamp;
    updatedAt: Timestamp;
}

export interface DailyUptime {
    date: string; // YYYY-MM-DD
    uptimePercentage: number;
    status: ComponentStatus;
}

export interface IncidentUpdate {
    id: string;
    status: IncidentStatus;
    message: string;
    createdAt: Timestamp;
    createdBy: string;
}

export interface Incident {
    id: string;
    title: string;
    status: IncidentStatus;
    impact: IncidentImpact;
    affectedComponents: string[];
    updates: IncidentUpdate[];
    createdAt: Timestamp;
    updatedAt: Timestamp;
    resolvedAt: Timestamp | null;
    createdBy: string;
}

export interface ScheduledMaintenance {
    id: string;
    title: string;
    description: string;
    status: MaintenanceStatus;
    affectedComponents: string[];
    scheduledStartTime: Timestamp;
    scheduledEndTime: Timestamp;
    actualStartTime: Timestamp | null;
    actualEndTime: Timestamp | null;
    createdAt: Timestamp;
    updatedAt: Timestamp;
    createdBy: string;
}

export interface StatusSubscriber {
    id: string;
    email: string;
    components: string[] | 'all';
    notifyOnIncidents: boolean;
    notifyOnMaintenance: boolean;
    notifyOnResolution: boolean;
    isVerified: boolean;
    createdAt: Timestamp;
    unsubscribedAt: Timestamp | null;
}

export interface StatusLog {
    id: string;
    componentId: string;
    status: ComponentStatus;
    checkedAt: Timestamp;
    responseTime: number;
    isHealthy: boolean;
    errorMessage?: string;
}

export interface OverallStatus {
    status: ComponentStatus;
    message: string;
    activeIncidents: number;
    scheduledMaintenance: number;
}

// ============================================================================
// COLLECTION REFERENCES
// ============================================================================

const COLLECTIONS = {
    components: 'status_components',
    incidents: 'incidents',
    maintenance: 'scheduled_maintenance',
    subscribers: 'status_subscribers',
    logs: 'status_logs'
};

// ============================================================================
// STATUS COMPONENT OPERATIONS
// ============================================================================

/**
 * Get all active status components
 */
export const getStatusComponents = async (): Promise<StatusComponent[]> => {
    try {
        // Simple query without composite index requirement
        const snapshot = await getDocs(collection(db, COLLECTIONS.components));
        const components = snapshot.docs
            .map(doc => ({
                id: doc.id,
                ...doc.data()
            } as StatusComponent))
            .filter(c => c.isActive)
            .sort((a, b) => a.order - b.order);

        return components;
    } catch (error) {
        console.error('Error fetching status components:', error);
        return [];
    }
};

/**
 * Subscribe to real-time status component updates
 */
export const subscribeToStatusComponents = (
    callback: (components: StatusComponent[]) => void
): Unsubscribe => {
    // limit(50) caps read cost — a status page will never have more than
    // a handful of components; client-side filter+sort still applies.
    return onSnapshot(query(collection(db, COLLECTIONS.components), limit(50)), (snapshot) => {
        const components = snapshot.docs
            .map(doc => ({
                id: doc.id,
                ...doc.data()
            } as StatusComponent))
            .filter(c => c.isActive)
            .sort((a, b) => a.order - b.order);

        callback(components);
    }, (error) => {
        console.error('Error in status components subscription:', error);
        callback([]);
    });
};

/**
 * Get components by category
 */
export const getComponentsByCategory = async (category: ComponentCategory): Promise<StatusComponent[]> => {
    try {
        const q = query(
            collection(db, COLLECTIONS.components),
            where('isActive', '==', true),
            where('category', '==', category),
            orderBy('order', 'asc')
        );

        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as StatusComponent));
    } catch (error) {
        console.error('Error fetching components by category:', error);
        return [];
    }
};

/**
 * Update component status (admin only)
 */
export const updateComponentStatus = async (
    componentId: string,
    status: ComponentStatus,
    userId: string
): Promise<boolean> => {
    try {
        const componentRef = doc(db, COLLECTIONS.components, componentId);
        await updateDoc(componentRef, {
            status,
            lastStatusChangeAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });

        // Log the status change
        await addDoc(collection(db, COLLECTIONS.logs), {
            componentId,
            status,
            checkedAt: serverTimestamp(),
            responseTime: 0,
            isHealthy: status === 'operational',
            updatedBy: userId
        });

        return true;
    } catch (error) {
        console.error('Error updating component status:', error);
        return false;
    }
};

/**
 * Create a new component (admin only)
 */
export const createComponent = async (
    component: Omit<StatusComponent, 'id' | 'createdAt' | 'updatedAt' | 'dailyUptime'>
): Promise<string | null> => {
    try {
        const docRef = await addDoc(collection(db, COLLECTIONS.components), {
            ...component,
            dailyUptime: [],
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });
        return docRef.id;
    } catch (error) {
        console.error('Error creating component:', error);
        return null;
    }
};

/**
 * Delete a component (admin only)
 */
export const deleteComponent = async (componentId: string): Promise<boolean> => {
    try {
        await deleteDoc(doc(db, COLLECTIONS.components, componentId));
        return true;
    } catch (error) {
        console.error('Error deleting component:', error);
        return false;
    }
};

// ============================================================================
// INCIDENT OPERATIONS
// ============================================================================

/**
 * Get active incidents
 */
export const getActiveIncidents = async (): Promise<Incident[]> => {
    try {
        const q = query(
            collection(db, COLLECTIONS.incidents),
            where('status', '!=', 'resolved'),
            orderBy('status'),
            orderBy('createdAt', 'desc')
        );

        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as Incident));
    } catch (error) {
        console.error('Error fetching active incidents:', error);
        return [];
    }
};

/**
 * Subscribe to real-time incident updates
 */
export const subscribeToIncidents = (
    callback: (incidents: Incident[]) => void
): Unsubscribe => {
    const q = query(
        collection(db, COLLECTIONS.incidents),
        orderBy('createdAt', 'desc'),
        limit(20)
    );

    return onSnapshot(q, (snapshot) => {
        const incidents = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as Incident));
        callback(incidents);
    });
};

/**
 * Get past incidents (last 14 days)
 */
export const getPastIncidents = async (days: number = 14): Promise<Incident[]> => {
    try {
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - days);

        const q = query(
            collection(db, COLLECTIONS.incidents),
            where('status', '==', 'resolved'),
            where('resolvedAt', '>=', Timestamp.fromDate(cutoffDate)),
            orderBy('resolvedAt', 'desc')
        );

        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as Incident));
    } catch (error) {
        console.error('Error fetching past incidents:', error);
        return [];
    }
};

/**
 * Create a new incident (admin only)
 */
export const createIncident = async (
    title: string,
    impact: IncidentImpact,
    affectedComponents: string[],
    initialMessage: string,
    createdBy: string
): Promise<string | null> => {
    try {
        const now = Timestamp.now();
        const initialUpdate: IncidentUpdate = {
            id: `update_${Date.now()}`,
            status: 'investigating',
            message: initialMessage,
            createdAt: now,
            createdBy
        };

        const docRef = await addDoc(collection(db, COLLECTIONS.incidents), {
            title,
            status: 'investigating',
            impact,
            affectedComponents,
            updates: [initialUpdate],
            createdAt: now,
            updatedAt: now,
            resolvedAt: null,
            createdBy
        });

        // Update affected component statuses
        for (const componentId of affectedComponents) {
            const newStatus: ComponentStatus =
                impact === 'critical' ? 'major_outage' :
                    impact === 'major' ? 'partial_outage' :
                        'degraded';
            await updateComponentStatus(componentId, newStatus, createdBy);
        }

        return docRef.id;
    } catch (error) {
        console.error('Error creating incident:', error);
        return null;
    }
};

/**
 * Add update to incident (admin only)
 */
export const addIncidentUpdate = async (
    incidentId: string,
    status: IncidentStatus,
    message: string,
    createdBy: string
): Promise<boolean> => {
    try {
        const incidentRef = doc(db, COLLECTIONS.incidents, incidentId);
        const incidentDoc = await getDoc(incidentRef);

        if (!incidentDoc.exists()) return false;

        const incident = incidentDoc.data() as Incident;
        const now = Timestamp.now();

        const newUpdate: IncidentUpdate = {
            id: `update_${Date.now()}`,
            status,
            message,
            createdAt: now,
            createdBy
        };

        const updateData: any = {
            status,
            updates: [...incident.updates, newUpdate],
            updatedAt: now
        };

        if (status === 'resolved') {
            updateData.resolvedAt = now;

            // Reset affected component statuses to operational
            for (const componentId of incident.affectedComponents) {
                await updateComponentStatus(componentId, 'operational', createdBy);
            }
        }

        await updateDoc(incidentRef, updateData);
        return true;
    } catch (error) {
        console.error('Error adding incident update:', error);
        return false;
    }
};

// ============================================================================
// SCHEDULED MAINTENANCE OPERATIONS
// ============================================================================

/**
 * Get upcoming and active maintenance
 */
export const getScheduledMaintenance = async (): Promise<ScheduledMaintenance[]> => {
    try {
        const q = query(
            collection(db, COLLECTIONS.maintenance),
            where('status', 'in', ['scheduled', 'in_progress']),
            orderBy('scheduledStartTime', 'asc')
        );

        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as ScheduledMaintenance));
    } catch (error) {
        console.error('Error fetching scheduled maintenance:', error);
        return [];
    }
};

/**
 * Subscribe to maintenance updates
 */
export const subscribeToMaintenance = (
    callback: (maintenance: ScheduledMaintenance[]) => void
): Unsubscribe => {
    const q = query(
        collection(db, COLLECTIONS.maintenance),
        where('status', 'in', ['scheduled', 'in_progress']),
        orderBy('scheduledStartTime', 'asc')
    );

    return onSnapshot(q, (snapshot) => {
        const maintenance = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as ScheduledMaintenance));
        callback(maintenance);
    });
};

/**
 * Create scheduled maintenance (admin only)
 */
export const createMaintenance = async (
    title: string,
    description: string,
    affectedComponents: string[],
    scheduledStartTime: Date,
    scheduledEndTime: Date,
    createdBy: string
): Promise<string | null> => {
    try {
        const docRef = await addDoc(collection(db, COLLECTIONS.maintenance), {
            title,
            description,
            status: 'scheduled',
            affectedComponents,
            scheduledStartTime: Timestamp.fromDate(scheduledStartTime),
            scheduledEndTime: Timestamp.fromDate(scheduledEndTime),
            actualStartTime: null,
            actualEndTime: null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            createdBy
        });
        return docRef.id;
    } catch (error) {
        console.error('Error creating maintenance:', error);
        return null;
    }
};

/**
 * Start maintenance (admin only)
 */
export const startMaintenance = async (
    maintenanceId: string,
    userId: string
): Promise<boolean> => {
    try {
        const maintenanceRef = doc(db, COLLECTIONS.maintenance, maintenanceId);
        const maintenanceDoc = await getDoc(maintenanceRef);

        if (!maintenanceDoc.exists()) return false;

        const maintenance = maintenanceDoc.data() as ScheduledMaintenance;

        await updateDoc(maintenanceRef, {
            status: 'in_progress',
            actualStartTime: serverTimestamp(),
            updatedAt: serverTimestamp()
        });

        // Update affected component statuses to maintenance
        for (const componentId of maintenance.affectedComponents) {
            await updateComponentStatus(componentId, 'maintenance', userId);
        }

        return true;
    } catch (error) {
        console.error('Error starting maintenance:', error);
        return false;
    }
};

/**
 * Complete maintenance (admin only)
 */
export const completeMaintenance = async (
    maintenanceId: string,
    userId: string
): Promise<boolean> => {
    try {
        const maintenanceRef = doc(db, COLLECTIONS.maintenance, maintenanceId);
        const maintenanceDoc = await getDoc(maintenanceRef);

        if (!maintenanceDoc.exists()) return false;

        const maintenance = maintenanceDoc.data() as ScheduledMaintenance;

        await updateDoc(maintenanceRef, {
            status: 'completed',
            actualEndTime: serverTimestamp(),
            updatedAt: serverTimestamp()
        });

        // Reset affected component statuses to operational
        for (const componentId of maintenance.affectedComponents) {
            await updateComponentStatus(componentId, 'operational', userId);
        }

        return true;
    } catch (error) {
        console.error('Error completing maintenance:', error);
        return false;
    }
};

// ============================================================================
// SUBSCRIBER OPERATIONS
// ============================================================================

/**
 * Subscribe to status notifications
 */
export const subscribeToNotifications = async (
    email: string,
    components: string[] | 'all' = 'all',
    notifyOnIncidents: boolean = true,
    notifyOnMaintenance: boolean = true,
    notifyOnResolution: boolean = true
): Promise<{ success: boolean; message: string }> => {
    try {
        const normalizedEmail = email.toLowerCase().trim();

        // Try to check if already subscribed, but handle permission errors gracefully
        let alreadySubscribed = false;
        try {
            const snapshot = await getDocs(collection(db, COLLECTIONS.subscribers));
            const existing = snapshot.docs.find(doc => {
                const data = doc.data();
                return data.email === normalizedEmail && data.unsubscribedAt === null;
            });
            alreadySubscribed = !!existing;
        } catch (checkError) {
            // If we can't check for duplicates (permission error), just proceed with subscription
            console.log('Could not check for existing subscription, proceeding to subscribe');
        }

        if (alreadySubscribed) {
            return { success: true, message: 'You are already subscribed to status updates!' };
        }

        await addDoc(collection(db, COLLECTIONS.subscribers), {
            email: normalizedEmail,
            components,
            notifyOnIncidents,
            notifyOnMaintenance,
            notifyOnResolution,
            isVerified: false,
            createdAt: serverTimestamp(),
            unsubscribedAt: null
        });

        return { success: true, message: 'Successfully subscribed to status updates!' };
    } catch (error: any) {
        console.error('Error subscribing to notifications:', error);

        // Check if it's a permission error
        if (error?.code === 'permission-denied') {
            return { success: false, message: 'Unable to subscribe. Please try again later.' };
        }

        return { success: false, message: 'Failed to subscribe. Please try again.' };
    }
};

/**
 * Unsubscribe from status notifications
 */
export const unsubscribeFromNotifications = async (
    subscriberId: string
): Promise<boolean> => {
    try {
        const subscriberRef = doc(db, COLLECTIONS.subscribers, subscriberId);
        await updateDoc(subscriberRef, {
            unsubscribedAt: serverTimestamp()
        });
        return true;
    } catch (error) {
        console.error('Error unsubscribing:', error);
        return false;
    }
};

/**
 * Get all active subscribers (admin only)
 */
export const getActiveSubscribers = async (): Promise<StatusSubscriber[]> => {
    try {
        // Simple query - filter and sort client-side to avoid composite index requirements
        const snapshot = await getDocs(collection(db, COLLECTIONS.subscribers));
        const subscribers = snapshot.docs
            .map(doc => ({
                id: doc.id,
                ...doc.data()
            } as StatusSubscriber))
            .filter(sub => sub.unsubscribedAt === null)
            .sort((a, b) => {
                const aTime = a.createdAt?.toDate?.()?.getTime() || 0;
                const bTime = b.createdAt?.toDate?.()?.getTime() || 0;
                return bTime - aTime; // Descending order
            });

        return subscribers;
    } catch (error) {
        console.error('Error fetching subscribers:', error);
        return [];
    }
};

// ============================================================================
// OVERALL STATUS
// ============================================================================

/**
 * Calculate overall system status
 */
export const getOverallStatus = (components: StatusComponent[]): OverallStatus => {
    if (components.length === 0) {
        return {
            status: 'operational',
            message: 'All systems operational',
            activeIncidents: 0,
            scheduledMaintenance: 0
        };
    }

    const statusPriority: Record<ComponentStatus, number> = {
        major_outage: 4,
        partial_outage: 3,
        degraded: 2,
        maintenance: 1,
        operational: 0
    };

    let worstStatus: ComponentStatus = 'operational';
    let worstPriority = 0;

    for (const component of components) {
        const priority = statusPriority[component.status];
        if (priority > worstPriority) {
            worstPriority = priority;
            worstStatus = component.status;
        }
    }

    const messages: Record<ComponentStatus, string> = {
        operational: 'All systems operational',
        degraded: 'Some systems experiencing degraded performance',
        partial_outage: 'Partial system outage',
        major_outage: 'Major system outage',
        maintenance: 'Scheduled maintenance in progress'
    };

    return {
        status: worstStatus,
        message: messages[worstStatus],
        activeIncidents: 0, // Will be updated by caller
        scheduledMaintenance: 0 // Will be updated by caller
    };
};

/**
 * Get status color for a given status
 */
export const getStatusColor = (status: ComponentStatus): string => {
    const colors: Record<ComponentStatus, string> = {
        operational: '#22C55E',
        degraded: '#EAB308',
        partial_outage: '#F97316',
        major_outage: '#EF4444',
        maintenance: '#3B82F6'
    };
    return colors[status];
};

/**
 * Get status background color (lighter) for a given status
 */
export const getStatusBgColor = (status: ComponentStatus): string => {
    const colors: Record<ComponentStatus, string> = {
        operational: '#DCFCE7',
        degraded: '#FEF9C3',
        partial_outage: '#FFEDD5',
        major_outage: '#FEE2E2',
        maintenance: '#DBEAFE'
    };
    return colors[status];
};

/**
 * Get human-readable status label
 */
export const getStatusLabel = (status: ComponentStatus): string => {
    const labels: Record<ComponentStatus, string> = {
        operational: 'Operational',
        degraded: 'Degraded Performance',
        partial_outage: 'Partial Outage',
        major_outage: 'Major Outage',
        maintenance: 'Under Maintenance'
    };
    return labels[status];
};

// ============================================================================
// UPTIME CALCULATIONS
// ============================================================================

/**
 * Calculate uptime percentage from daily uptime data
 */
export const calculateUptimePercentage = (dailyUptime: DailyUptime[], days: number): number => {
    if (!dailyUptime || dailyUptime.length === 0) return 100;

    const recentDays = dailyUptime.slice(-days);
    if (recentDays.length === 0) return 100;

    const totalUptime = recentDays.reduce((sum, day) => sum + day.uptimePercentage, 0);
    return Math.round((totalUptime / recentDays.length) * 100) / 100;
};

/**
 * Get the 90-day uptime chart data for a component
 */
export const get90DayUptimeChart = (component: StatusComponent): DailyUptime[] => {
    if (!component.dailyUptime || component.dailyUptime.length === 0) {
        // Generate mock data for 90 days (all operational)
        const data: DailyUptime[] = [];
        for (let i = 89; i >= 0; i--) {
            const date = new Date();
            date.setDate(date.getDate() - i);
            data.push({
                date: date.toISOString().split('T')[0],
                uptimePercentage: 100,
                status: 'operational'
            });
        }
        return data;
    }

    return component.dailyUptime.slice(-90);
};

// ============================================================================
// ADMIN SEED DATA
// ============================================================================

/**
 * Seed initial status components (admin only, run once)
 * Checks if each component already exists to prevent duplicates
 */
export const seedStatusComponents = async (userId: string): Promise<boolean> => {
    try {
        // First, check what components already exist
        const existingSnapshot = await getDocs(collection(db, COLLECTIONS.components));
        const existingNames = new Set(existingSnapshot.docs.map(doc => doc.data().name));

        const components: Omit<StatusComponent, 'id' | 'createdAt' | 'updatedAt' | 'dailyUptime'>[] = [
            // Channels
            { name: 'Email', description: 'Email sending via AWS SES', category: 'channels', status: 'operational', order: 1, isActive: true, showUptimeChart: true, lastCheckedAt: null, lastStatusChangeAt: null, uptimePercentage30Days: 100, uptimePercentage90Days: 100 },
            { name: 'Webhooks', description: 'Outbound webhook delivery', category: 'channels', status: 'operational', order: 2, isActive: true, showUptimeChart: true, lastCheckedAt: null, lastStatusChangeAt: null, uptimePercentage30Days: 100, uptimePercentage90Days: 100 },

            // Platforms
            { name: 'Shopify', description: 'Shopify store integration', category: 'platforms', status: 'operational', order: 10, isActive: true, showUptimeChart: true, lastCheckedAt: null, lastStatusChangeAt: null, uptimePercentage30Days: 100, uptimePercentage90Days: 100 },
            { name: 'WooCommerce', description: 'WooCommerce integration', category: 'platforms', status: 'operational', order: 11, isActive: true, showUptimeChart: true, lastCheckedAt: null, lastStatusChangeAt: null, uptimePercentage30Days: 100, uptimePercentage90Days: 100 },
            { name: 'Wix', description: 'Wix store integration', category: 'platforms', status: 'operational', order: 12, isActive: true, showUptimeChart: true, lastCheckedAt: null, lastStatusChangeAt: null, uptimePercentage30Days: 100, uptimePercentage90Days: 100 },
            { name: 'Typeform', description: 'Form responses sync', category: 'platforms', status: 'operational', order: 13, isActive: true, showUptimeChart: true, lastCheckedAt: null, lastStatusChangeAt: null, uptimePercentage30Days: 100, uptimePercentage90Days: 100 },
            { name: 'Klaviyo', description: 'Contact import', category: 'platforms', status: 'operational', order: 14, isActive: true, showUptimeChart: true, lastCheckedAt: null, lastStatusChangeAt: null, uptimePercentage30Days: 100, uptimePercentage90Days: 100 },
            { name: 'Mailchimp', description: 'Contact import', category: 'platforms', status: 'operational', order: 15, isActive: true, showUptimeChart: true, lastCheckedAt: null, lastStatusChangeAt: null, uptimePercentage30Days: 100, uptimePercentage90Days: 100 },
            { name: 'Google Forms', description: 'Form integration', category: 'platforms', status: 'operational', order: 16, isActive: true, showUptimeChart: true, lastCheckedAt: null, lastStatusChangeAt: null, uptimePercentage30Days: 100, uptimePercentage90Days: 100 },
            { name: 'Zapier', description: 'Zapier webhooks', category: 'platforms', status: 'operational', order: 17, isActive: true, showUptimeChart: true, lastCheckedAt: null, lastStatusChangeAt: null, uptimePercentage30Days: 100, uptimePercentage90Days: 100 },

            // Product Areas
            { name: 'API', description: 'REST API endpoints', category: 'product_areas', status: 'operational', order: 20, isActive: true, showUptimeChart: true, lastCheckedAt: null, lastStatusChangeAt: null, uptimePercentage30Days: 100, uptimePercentage90Days: 100 },
            { name: 'Campaigns', description: 'Email campaigns', category: 'product_areas', status: 'operational', order: 21, isActive: true, showUptimeChart: true, lastCheckedAt: null, lastStatusChangeAt: null, uptimePercentage30Days: 100, uptimePercentage90Days: 100 },
            { name: 'Automation', description: 'Automation engine', category: 'product_areas', status: 'operational', order: 22, isActive: true, showUptimeChart: true, lastCheckedAt: null, lastStatusChangeAt: null, uptimePercentage30Days: 100, uptimePercentage90Days: 100 },
            { name: 'Dashboard', description: 'Web application', category: 'product_areas', status: 'operational', order: 23, isActive: true, showUptimeChart: true, lastCheckedAt: null, lastStatusChangeAt: null, uptimePercentage30Days: 100, uptimePercentage90Days: 100 },
        ];

        let addedCount = 0;
        let skippedCount = 0;

        for (const component of components) {
            if (existingNames.has(component.name)) {
                console.log(`Skipping ${component.name} - already exists`);
                skippedCount++;
                continue;
            }
            await createComponent(component);
            addedCount++;
        }

        console.log(`Status components seeded: ${addedCount} added, ${skippedCount} skipped`);
        return true;
    } catch (error) {
        console.error('Error seeding status components:', error);
        return false;
    }
};

/**
 * Delete all components and re-seed fresh (admin only)
 * Use this to clean up duplicates
 */
export const cleanupAndReseedComponents = async (userId: string): Promise<boolean> => {
    try {
        // Delete all existing components
        const snapshot = await getDocs(collection(db, COLLECTIONS.components));
        console.log(`Deleting ${snapshot.docs.length} existing components...`);

        for (const docSnap of snapshot.docs) {
            await deleteDoc(doc(db, COLLECTIONS.components, docSnap.id));
        }

        console.log('All components deleted, re-seeding...');

        // Re-seed fresh
        return await seedStatusComponents(userId);
    } catch (error) {
        console.error('Error cleaning up components:', error);
        return false;
    }
};

export default {
    // Components
    getStatusComponents,
    subscribeToStatusComponents,
    getComponentsByCategory,
    updateComponentStatus,
    createComponent,
    deleteComponent,

    // Incidents
    getActiveIncidents,
    subscribeToIncidents,
    getPastIncidents,
    createIncident,
    addIncidentUpdate,

    // Maintenance
    getScheduledMaintenance,
    subscribeToMaintenance,
    createMaintenance,
    startMaintenance,
    completeMaintenance,

    // Subscribers
    subscribeToNotifications,
    unsubscribeFromNotifications,
    getActiveSubscribers,

    // Utilities
    getOverallStatus,
    getStatusColor,
    getStatusBgColor,
    getStatusLabel,
    calculateUptimePercentage,
    get90DayUptimeChart,

    // Admin
    seedStatusComponents,
    cleanupAndReseedComponents
};
